import type { UseQueryResult } from 'react-query';
import { useQuery } from 'react-query';
import * as Sentry from '@sentry/browser';
import { queryClient } from '@hotjar/webapp/utils/react-query';
import type { FeedbackSummary } from '@hotjar/webapp/domains/activation/feedback-widgets/types';
import { getFeedbackSummary } from '@hotjar/webapp/domains/activation/feedback-widgets/api';
import { useFeatureFlag } from '@hotjar/feature-flags';

const CACHE_TIME = 60 * 1000; // 1 min

export const INCOMING_FEEDBACK_SUMMARY_QUERY_KEY = 'incomingFeedbackSummary';

export const useFeedbackSummary = (siteId?: SiteId): UseQueryResult<FeedbackSummary, Error> => {
  const shouldUseSeparateAskService = useFeatureFlag('ask.separate_service');
  return useQuery(
    [INCOMING_FEEDBACK_SUMMARY_QUERY_KEY, { siteId }],
    () => getFeedbackSummary(siteId as SiteId, shouldUseSeparateAskService),
    {
      retry: false,
      enabled: !!siteId,
      cacheTime: CACHE_TIME,
      refetchOnWindowFocus: false,
    },
  );
};

export const invalidateFeedbackSummaryCache = (): void => {
  try {
    // Invalidate cache to make sure we show the new/updated feedback summary on the overview page
    queryClient.invalidateQueries(INCOMING_FEEDBACK_SUMMARY_QUERY_KEY);
  } catch {
    Sentry.captureMessage('Error invalidating feedback summary cache', Sentry.Severity.Info);
  }
};
